const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function startOfLocalDay(value) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfLocalDay(value) {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function wholeDaysBetween(from, to) {
  return Math.round((startOfLocalDay(to) - startOfLocalDay(from)) / MS_PER_DAY);
}

/** Pixel X of today on the timeline, or null if outside the range. */
export function todayXOffset(rangeStart, rangeEnd, dayWidth, now = new Date()) {
  if (!rangeStart || !rangeEnd || !dayWidth) return null;
  const today = startOfLocalDay(now);
  if (today < startOfLocalDay(rangeStart) || today > endOfLocalDay(rangeEnd)) return null;
  return wholeDaysBetween(rangeStart, today) * dayWidth;
}

/** Grow min/max so today is never clipped off the Gantt. */
export function expandRangeToIncludeToday(minDate, maxDate, now = new Date()) {
  const today = startOfLocalDay(now);
  let min = new Date(minDate);
  let max = new Date(maxDate);
  if (today < startOfLocalDay(min)) {
    min = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  }
  if (today > endOfLocalDay(max)) {
    max = new Date(today.getFullYear(), today.getMonth() + 2, 0);
  }
  return { min, max };
}

export function clampScroll(el, left, top) {
  if (!el) return { left: 0, top: 0 };
  const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
  const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
  return {
    left: Math.max(0, Math.min(left, maxLeft)),
    top: Math.max(0, Math.min(top, maxTop)),
  };
}

export function easeInOutCubic(t) {
  const x = Math.max(0, Math.min(1, t));
  return x < 0.5 ? 4 * x * x * x : 1 - ((-2 * x + 2) ** 3) / 2;
}

/** Duration in ms from pixel distance: short hops stay snappy, long jumps cap at 520ms. */
export function scrollDurationMs(deltaPx) {
  return Math.max(240, Math.min(520, 180 + Math.abs(deltaPx) * 0.28));
}

/**
 * Animate scroll of one or more synced elements.
 * `apply({ left, top, progress })` is called every frame.
 * Returns a cancel() function.
 */
export function animateSyncedScroll({
  fromLeft,
  fromTop,
  toLeft,
  toTop,
  duration,
  apply,
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  raf = (fn) => requestAnimationFrame(fn),
  caf = (id) => cancelAnimationFrame(id),
}) {
  const dist = Math.max(Math.abs(toLeft - fromLeft), Math.abs(toTop - fromTop));
  const ms = duration ?? scrollDurationMs(dist);
  let frame = 0;
  let stopped = false;
  const t0 = now();

  const tick = (t) => {
    if (stopped) return;
    const p = ms <= 0 ? 1 : Math.min(1, (t - t0) / ms);
    const e = easeInOutCubic(p);
    apply({
      left: fromLeft + (toLeft - fromLeft) * e,
      top: fromTop + (toTop - fromTop) * e,
      progress: p,
    });
    if (p < 1) frame = raf(tick);
  };

  frame = raf(tick);
  return () => {
    stopped = true;
    if (frame) caf(frame);
  };
}
